
(() => {
  const RM = '(prefers-reduced-motion: reduce)';
  const DESK = '(min-width: 769px)';
  const reduced = () => window.matchMedia(RM).matches;

  const fontsReady = () =>
    ((document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve());

  const imagesReady = (el) => {
    const imgs = [...el.querySelectorAll('img')].filter((i) => !i.complete);
    if (!imgs.length) return Promise.resolve();
    return Promise.all(imgs.map((i) => new Promise((res) => {
      i.addEventListener('load', res, { once: true });
      i.addEventListener('error', res, { once: true });
    })));
  };

  const markCurrent = (el, on) => {
    el.classList.toggle('is-active', on);
    if (el.getAttribute('role') === 'tab') el.setAttribute('aria-selected', on ? 'true' : 'false');
    else if (el.hasAttribute('aria-current')) el.setAttribute('aria-current', on ? 'true' : 'false');
  };

  const markDisabled = (btn, off) => {
    if (!btn) return;
    btn.toggleAttribute('disabled', off);
    if (btn.tagName !== 'BUTTON') btn.setAttribute('aria-disabled', off ? 'true' : 'false');
  };

  class EmblaSlider extends HTMLElement {
    connectedCallback() {

      if (this.embla || this.ticker) return;
      this.viewport = this.querySelector('.embla__viewport');
      if (!this.viewport || typeof EmblaCarousel === 'undefined') return;
      if (this.hasAttribute('data-auto-scroll')) {
        this.startTicker();
        return;
      }
      this.init();
    }

    get jump() {
      return this.hasAttribute('data-fade') && reduced();
    }

    readOptions() {
      let options = { align: 'start', containScroll: 'trimSnaps' };
      try { options = { ...options, ...JSON.parse(this.dataset.options || '{}') }; }
      catch (e) { console.warn('[embla-slider] bad data-options JSON', e); }
      return options;
    }

    buildPlugins() {
      const plugins = [];
      const rm = reduced();
      const autoplayMs = Number(this.dataset.autoplay || 0);
      const keepWhenReduced = this.dataset.autoplayReduced === 'jump';
      if (autoplayMs > 0 && typeof EmblaCarouselAutoplay !== 'undefined' && (!rm || keepWhenReduced)) {
        plugins.push(EmblaCarouselAutoplay({
          delay: autoplayMs,
          stopOnInteraction: false,
          stopOnMouseEnter: this.dataset.autoplayHover !== 'play',
          jump: rm,
        }));
      }

      const container = this.viewport.firstElementChild;
      if (this.hasAttribute('data-fade') && typeof EmblaCarouselFade !== 'undefined'
          && container && container.children.length) {
        plugins.push(EmblaCarouselFade());
      }
      if (this.hasAttribute('data-fade-desktop') && typeof EmblaCarouselFade !== 'undefined'
          && container && container.children.length > 1) {
        plugins.push(EmblaCarouselFade({ active: false, breakpoints: { [DESK]: { active: true } } }));
      }
      return plugins;
    }

    init() {
      this.abort = new AbortController();
      this.embla = EmblaCarousel(this.viewport, this.readOptions(), this.buildPlugins());

      this.bindArrows();
      this.bindDots();
      this.bindKeys();
      this.embla.on('select', () => this.syncUI());
      this.embla.on('reInit', () => { this.buildDots(); this.syncUI(); });
      this.syncUI();
      this.watchVideoPause();

      this.classList.add('is-ready');
    }

    /* Поки в слайдері грає відео, автоперегортання стоїть: інакше ролик
       зрізало б на пʼятій секунді. Ознака - клас is-playing, його ставить і
       знімає sa-video.js на цьому ж елементі. */
    watchVideoPause() {
      const auto = this.embla?.plugins()?.autoplay;
      if (!auto) return;

      let playing = this.classList.contains('is-playing');
      if (playing) auto.stop();

      const watcher = new MutationObserver(() => {
        const now = this.classList.contains('is-playing');
        if (now === playing) return;
        playing = now;
        if (now) auto.stop();
        else auto.play();
      });
      watcher.observe(this, { attributes: true, attributeFilter: ['class'] });
      this.abort.signal.addEventListener('abort', () => watcher.disconnect());
    }

    get controlsRoot() {
      const sel = this.dataset.controls;
      return (sel && this.closest(sel)) || this;
    }

    bindArrows() {
      const root = this.controlsRoot;
      const { signal } = this.abort;
      this.prevBtn = root.querySelector('[data-embla-prev]');
      this.nextBtn = root.querySelector('[data-embla-next]');
      const wire = (btn, go) => {
        if (!btn) return;
        btn.addEventListener('click', go, { signal });

        if (btn.tagName !== 'BUTTON') {
          btn.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            go();
          }, { signal });
        }
      };
      wire(this.prevBtn, () => this.embla?.scrollPrev(this.jump));
      wire(this.nextBtn, () => this.embla?.scrollNext(this.jump));
    }

    bindDots() {
      const root = this.controlsRoot;
      this.dotsBox = root.querySelector('[data-embla-dots]');
      if (this.dotsBox) this.buildDots();
      this.bindThumbs(root);
    }

    bindThumbs(root) {
      const { signal } = this.abort;
      this.thumbs = [...root.querySelectorAll('[data-embla-goto]')];
      this.thumbs.forEach((b) => {
        b.addEventListener('click', () => this.embla?.scrollTo(Number(b.dataset.emblaGoto), this.jump), { signal });
      });
    }

    bindKeys() {
      if (!this.hasAttribute('data-keys')) return;
      this.viewport.addEventListener('keydown', (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        if (e.key === 'ArrowLeft') this.embla?.scrollPrev(this.jump);
        else this.embla?.scrollNext(this.jump);
      }, { signal: this.abort.signal });
    }

    buildDots() {
      if (!this.dotsBox) return;
      this.dotsBox.innerHTML = '';

      const label = this.dotsBox.dataset.slideLabel || this.dataset.slideLabel || 'Slide %n';
      this.dots = this.embla.scrollSnapList().map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'embla__dot';
        b.setAttribute('aria-label', label.replace('%n', String(i + 1)));
        b.setAttribute('aria-current', 'false');
        b.addEventListener('click', () => this.embla.scrollTo(i, this.jump));
        this.dotsBox.appendChild(b);
        return b;
      });
    }

    rescueFocus(other) {
      const usable = other && !other.hasAttribute('disabled') && other.getAttribute('aria-disabled') !== 'true'
        && !other.classList.contains('is-hidden') && other.getClientRects().length > 0;
      if (usable) { other.focus({ preventScroll: true }); return; }
      if (!this.viewport.hasAttribute('tabindex')) this.viewport.setAttribute('tabindex', '-1');
      this.viewport.focus({ preventScroll: true });
    }

    syncUI() {
      if (!this.embla) return;
      const canPrev = this.embla.canScrollPrev();
      const canNext = this.embla.canScrollNext();

      const active = document.activeElement;
      const losing = (active && active === this.prevBtn && !canPrev) || (active && active === this.nextBtn && !canNext);
      markDisabled(this.prevBtn, !canPrev);
      markDisabled(this.nextBtn, !canNext);
      if (losing) this.rescueFocus(active === this.prevBtn ? this.nextBtn : this.prevBtn);

      const single = this.embla.scrollSnapList().length <= 1;
      this.prevBtn?.classList.toggle('is-hidden', single);
      this.nextBtn?.classList.toggle('is-hidden', single);
      const selected = this.embla.selectedScrollSnap();
      this.dots?.forEach((d, i) => {
        d.classList.toggle('is-active', i === selected);
        d.setAttribute('aria-current', i === selected ? 'true' : 'false');
      });
      this.thumbs?.forEach((t) => markCurrent(t, Number(t.dataset.emblaGoto) === selected));

      if (this.hasAttribute('data-fade')) {
        this.embla.slideNodes().forEach((el, i) => el.toggleAttribute('inert', i !== selected));
      }

      this.embla.slideNodes().forEach((el, i) => {
        if (!el.hasAttribute('data-slide-video')) return;
        const v = el.querySelector('video');
        if (!v) return;
        if (i === selected) {
          v.muted = true;
          if (v.preload === 'none') v.preload = 'auto';
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else v.pause();
      });

      if (this.hasAttribute('data-product-gallery')) {
        this.embla.slideNodes().forEach((el, i) => el.classList.toggle('is-current', i === selected));
        this.classList.add('is-ready');
      }

      if (this.hasAttribute('data-video-sync')) this.syncVideo(selected);
    }

    syncVideo(index) {
      const slide = this.embla.slideNodes()[index];
      if (!slide) return;

      if (this.videoIndex !== undefined && this.videoIndex !== index && window.saVideo) {
        window.saVideo.stop(this);
      }
      this.videoIndex = index;
      const src = (slide.getAttribute('data-video-src') || '').trim();
      this.setAttribute('data-video-src', src);
      this.classList.toggle('has-video', !!src);

      this.querySelectorAll('.vb-poster').forEach((img) => img.classList.remove('vb-poster'));
      slide.querySelector('img')?.classList.add('vb-poster');
    }

    refresh() {
      if (this.ticker) this.buildTicker();
      else this.embla?.reInit();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.ticker) this.stopTicker();
      this.embla?.destroy();
      this.embla = null;
    }

    startTicker() {
      const t = { abort: new AbortController(), built: false, width: 0, hover: false, focus: false, visible: true };
      this.ticker = t;
      const { signal } = t.abort;

      if (window.Shopify && window.Shopify.designMode) this.dataset.hover = 'pause';

      Promise.all([fontsReady(), imagesReady(this)]).then(() => {
        if (this.ticker === t) this.buildTicker();
      });

      let timer = 0;
      t.ro = new ResizeObserver(() => {
        if (!t.built) return;
        if (Math.abs(this.getBoundingClientRect().width - t.width) < 0.5) return;
        clearTimeout(timer);
        timer = setTimeout(() => { if (this.ticker === t) this.buildTicker(); }, 120);
      });
      t.ro.observe(this);

      if ('IntersectionObserver' in window) {
        t.io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { t.visible = e.isIntersecting; });
          this.tickerPlayState();
        }, { rootMargin: '120px' });
        t.io.observe(this);
      }

      const mq = window.matchMedia(RM);
      const onMq = () => { if (this.ticker === t) this.buildTicker(); };
      if (mq.addEventListener) mq.addEventListener('change', onMq, { signal });
      else if (mq.addListener) {
        mq.addListener(onMq);
        signal.addEventListener('abort', () => mq.removeListener(onMq));
      }

      this.addEventListener('mouseenter', () => { t.hover = true; this.tickerPlayState(); }, { signal });
      this.addEventListener('mouseleave', () => { t.hover = false; this.tickerPlayState(); }, { signal });
      this.addEventListener('focusin', () => { t.focus = true; this.tickerPlayState(); }, { signal });
      this.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        t.focus = false;
        this.tickerPlayState();
      }, { signal });
    }

    buildTicker() {
      const t = this.ticker;
      if (!t) return;
      this.embla?.destroy();
      this.embla = null;
      this.classList.remove('is-run');

      const container = this.viewport.firstElementChild;
      if (!container) return;
      container.querySelectorAll(':scope > [data-embla-clone]').forEach((n) => n.remove());
      t.built = true;
      t.width = this.getBoundingClientRect().width;

      const originals = [...container.children];
      if (!originals.length || this.dataset.animate === 'off' || reduced()
          || typeof EmblaCarouselAutoScroll === 'undefined') return;

      const onlyOverflow = this.dataset.autoScrollWhen === 'overflow';
      if (onlyOverflow) this.classList.add('is-measuring');

      const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
      const setW = originals[originals.length - 1].getBoundingClientRect().right
        - originals[0].getBoundingClientRect().left + gap;
      const vpW = this.viewport.getBoundingClientRect().width;
      if (onlyOverflow) this.classList.remove('is-measuring');

      if (!(setW >= 8) || !(vpW > 0)) return;

      if (onlyOverflow && setW - gap <= vpW + 0.5) return;

      const copies = Math.min(100, Math.ceil(vpW / setW) + 2);
      const frag = document.createDocumentFragment();
      for (let i = 1; i < copies; i += 1) {
        originals.forEach((node) => {
          const c = node.cloneNode(true);
          c.setAttribute('data-embla-clone', '');
          c.setAttribute('aria-hidden', 'true');
          [c, ...c.querySelectorAll('*')].forEach((el) => {
            if (el.matches('a[href], button, [tabindex]')) el.setAttribute('tabindex', '-1');
            el.removeAttribute('data-shopify-editor-block');
          });
          frag.appendChild(c);
        });
      }
      container.appendChild(frag);
      this.classList.add('is-run');

      const pxPerSecond = Math.max(5, parseFloat(this.dataset.autoScroll) || 60);
      this.embla = EmblaCarousel(this.viewport, {
        loop: true,
        align: 'start',
        containScroll: false,
        watchDrag: false,
        watchResize: false,
        watchSlides: false,
        watchFocus: false,
      }, [EmblaCarouselAutoScroll({
        speed: pxPerSecond / 60,
        direction: this.dataset.direction === 'right' ? 'backward' : 'forward',
        startDelay: 0,
        playOnInit: false,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
      })]);
      this.tickerPlayState();
    }

    tickerPlayState() {
      const t = this.ticker;
      const auto = this.embla && this.embla.plugins().autoScroll;
      if (!t || !auto) return;
      const paused = this.dataset.hover === 'pause' && (t.hover || t.focus);
      const run = t.visible && !paused;
      if (run && !auto.isPlaying()) auto.play(0);
      else if (!run && auto.isPlaying()) auto.stop();
    }

    stopTicker() {
      const t = this.ticker;
      this.ticker = null;
      t.abort.abort();
      t.ro?.disconnect();
      t.io?.disconnect();
    }
  }

  if (!customElements.get('embla-slider')) customElements.define('embla-slider', EmblaSlider);

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('embla-slider').forEach((s) => s.refresh?.());
  });
})();
